import React from "react";
import { useAppStore } from "../../state/app-store";
import { filterClass, joinPath } from "../../utils/class-utils";
import { withReturnContext } from "./shared/link-context";
import { CardItemIdRoot } from "./shared/item-id-comment";
import { formatEventTime } from "./shared/format-event-time";
import { getTextAlignClass, resolveHorizontalContentAlign } from "../utils/content-alignment";
import type { EventCardProps } from "./types";

function buildIcsFilename(title: string, slug?: string): string {
  const base = (slug || title || "event")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
  return `${base || "event"}.ics`;
}

export function EventCard(props: EventCardProps) {
  const {
    eventItem,
    basePath = "/events",
    buttonColorClass = "primary",
    calendarButtonColorClass = "primary-100",
    paddingClass = "py-0",
    cardBgClass = "text-bg-white",
    cardBorderClass = "no-class",
    cardBorderColorClass = "no-class",
    contentAlignClass = "no-class",
  } = props;
  // Check if we have valid event item data (with non-empty values)
  const hasValidData = eventItem && (
    (eventItem.C_com_group_item_name && eventItem.C_com_group_item_name.trim()) ||
    (eventItem.page_title && eventItem.page_title.trim()) ||
    (eventItem.slug && eventItem.slug.trim()) ||
    (eventItem.start_date && eventItem.start_date.trim()) ||
    (eventItem.location && eventItem.location.trim())
  );
  const item = hasValidData ? eventItem : null;
  const pageId = useAppStore((state) => state.documentId);

  // Detect editor mode to disable links
  const isInEditor = typeof document !== "undefined" && document.body.classList.contains("is-easyblocks-editor");

  /**
   * ===== FRONTEND: CLASSES & STRUCTURE (EDIT HERE) =====
   */
  const buttonVariant = buttonColorClass || "primary";
  const calendarButtonVariant = calendarButtonColorClass || "primary-100";

  const containerClassName = [
    filterClass(paddingClass),
  ]
    .filter(Boolean)
    .join(" ");

  const hasCardBorder = filterClass(cardBorderClass) || filterClass(cardBorderColorClass);
  const resolvedContentAlignClass = getTextAlignClass(
    resolveHorizontalContentAlign(contentAlignClass, undefined, "start")
  );

  const cardClassName = [
    "events-row__card",
    "card",
    "rounded-4",
    "h-100",
    filterClass(cardBgClass),
    hasCardBorder ? "border" : "",
    filterClass(cardBorderClass),
    filterClass(cardBorderColorClass),
  ]
    .filter(Boolean)
    .join(" ");

  const cardBodyClassName = ["events-row__card-body", "card-body", "d-flex", "flex-column", resolvedContentAlignClass]
    .filter(Boolean)
    .join(" ");
  const placeholderCardStyle = { "--bs-bg-opacity": ".9" } as React.CSSProperties;

  const classes = {
    header: "events-row__header d-flex flex-column gap-2 mb-4",
    title: "events-row__title h6 mb-1",
    meta: "events-row__meta small d-flex flex-column gap-1",
    metaItem: "events-row__meta-item",
    cta: "events-row__cta mt-auto d-flex justify-content-between gap-3 flex-wrap",
    ctaLink: ["events-row__cta-link", "btn", "btn-sm", `btn-${buttonVariant}`]
      .filter(Boolean)
      .join(" "),
    calendarCta: ["events-row__calendar-cta", "btn", "btn-sm", `btn-${calendarButtonVariant}`]
      .filter(Boolean)
      .join(" "),
    ctaDisabled: "events-row__cta-disabled btn btn-sm btn-outline-secondary",
    emptyText: "small",
  };

  if (!item) {
    // In live view, hide the component if there's no item
    if (!isInEditor) {
      return null;
    }
    // In edit mode, show a message
    return (
      <div className={containerClassName}>
        <div className={cardClassName} style={placeholderCardStyle}>
          <div className={cardBodyClassName}>
            <div className={classes.emptyText}>No event selected. Pick an event to preview.</div>
          </div>
        </div>
      </div>
    );
  }

  const title = item.item_name ?? item.page_title ?? item.C_com_group_item_name ?? "(Untitled event)";

  const href = item.slug ? withReturnContext(joinPath(basePath, item.slug), pageId) : undefined;
  const eventIcs = typeof item.calendar_event_data_ics === "string" ? item.calendar_event_data_ics.trim() : "";
  const hasIcs = eventIcs.length > 0;
  const downloadIcs = () => {
    if (isInEditor || !hasIcs) return;
    const blob = new Blob([eventIcs], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildIcsFilename(title, item.slug);
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={containerClassName}>
      <CardItemIdRoot itemId={item.item_id} className={cardClassName}>
        <div className={cardBodyClassName}>
          <div className={classes.header}>
            <div className={classes.title}>{title}</div>
            <div className={classes.meta}>
              {item.start_date ? (
                <div className={classes.metaItem}>
                  Date: {item.start_date}
                  {(() => {
                    const startTime = formatEventTime(item.start_time);
                    return startTime ? ` ${startTime}` : "";
                  })()}
                  {item.end_date && item.end_date !== item.start_date ? ` - ${item.end_date}` : ""}
                </div>
              ) : null}
              {item.location ? (
                <div className={classes.metaItem} dangerouslySetInnerHTML={{ __html: "Location: " + item.location }} />
              ) : null}
            </div>
          </div>

          <div className={classes.cta}>
            {href ? (
              <a
                className={classes.ctaLink}
                href={href}
                onClick={(e) => {
                  if (isInEditor) {
                    e.preventDefault();
                  }
                }}
              >
                View Event
              </a>
            ) : (
              <button className={classes.ctaDisabled} type="button" disabled>
                Missing slug
              </button>
            )}
            {hasIcs ? (
              <button
                className={classes.calendarCta}
                type="button"
                onClick={downloadIcs}
                disabled={isInEditor}
              >
                Add to Calendar
              </button>
            ) : null}
          </div>
        </div>
      </CardItemIdRoot>
    </div>
  );
}
